import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import CategoryBadges from "@/components/CategoryBadges";
import { RefreshCw, Trophy, ChevronDown, ChevronRight } from "lucide-react";
import { useSeason } from "@/context/SeasonContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function ageFrom(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Date.now() - d.getTime();
  const age = new Date(diff).getUTCFullYear() - 1970;
  return age;
}

export default function AthleteCategoryPanel({ athlete, onRecomputed }) {
  const { selectedSeasonId, currentSeason, activeSeason, seasons } = useSeason();
  const { user } = useAuth();
  const [computed, setComputed] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [paralympic, setParalympic] = useState(null);

  const canEdit = user && ["admin", "coach", "trainer"].includes(user.role);
  const isAdmin = user?.role === "admin";

  // Fallback robusto: se selectedSeasonId non è valido, usa la stagione attiva
  const effectiveSeasonId = useMemo(() => {
    if (selectedSeasonId && (seasons || []).some((s) => s.id === selectedSeasonId)) return selectedSeasonId;
    return activeSeason?.id || null;
  }, [selectedSeasonId, seasons, activeSeason]);

  const effectiveSeason = useMemo(() => {
    return (seasons || []).find((s) => s.id === effectiveSeasonId) || currentSeason || activeSeason;
  }, [seasons, effectiveSeasonId, currentSeason, activeSeason]);

  const refresh = async () => {
    if (!athlete?.id || !effectiveSeasonId) return;
    try {
      const [c, h, p] = await Promise.all([
        api.get(`/athletes/${athlete.id}/computed-categories?season_id=${effectiveSeasonId}`),
        api.get(`/athletes/${athlete.id}/season-categories`),
        api.get(`/athletes/${athlete.id}/paralympic-profile`).catch(() => ({ data: null })),
      ]);
      setComputed(c.data);
      setHistory(h.data || []);
      setParalympic(p.data);
    } catch (_) {}
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athlete?.id, effectiveSeasonId, athlete?.birth_date, athlete?.gender]);

  const recompute = async () => {
    if (!athlete?.id) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/athletes/${athlete.id}/recompute-category`, { season_id: effectiveSeasonId });
      toast.success(`Categoria aggiornata (${(data.applied || []).length})`);
      await refresh();
      if (onRecomputed) onRecomputed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore ricalcolo");
    } finally { setLoading(false); }
  };

  const age = ageFrom(athlete?.birth_date);
  const isParalympic = paralympic?.is_paralympic;
  const currentSeasonAssignments = (history || []).filter((h) => h.season_id === effectiveSeasonId);
  const otherSeasonAssignments = (history || []).filter((h) => h.season_id !== effectiveSeasonId);

  // Auto-heal contraddizione: se computed vuoto ma storico esistente per stagione corrente,
  // trigger silente di recompute UNA volta (usa flag su athlete.id per non loopare).
  useEffect(() => {
    if (!athlete?.id || !effectiveSeasonId || !computed) return;
    const noComputed = !(computed.categories && computed.categories.length);
    const hasHistory = currentSeasonAssignments.length > 0;
    if (noComputed && hasHistory && athlete?.birth_date && !loading) {
      // Sync: la history contiene categorie diverse da quelle calcolate ora.
      // Ricalcoliamo silente per rimuovere l'incongruenza.
      (async () => {
        try {
          await api.post(`/athletes/${athlete.id}/recompute-category`, { season_id: effectiveSeasonId });
          await refresh();
          if (onRecomputed) onRecomputed();
        } catch (_) {}
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computed, effectiveSeasonId, athlete?.id]);

  // Categorie da mostrare: preferisci computed se non vuoto, altrimenti storico stagione corrente
  const displayCategories = (computed?.categories && computed.categories.length)
    ? computed.categories
    : currentSeasonAssignments.map((c) => ({
        category_id: c.category_id, category_code: c.category_code, display_name: c.display_name, is_primary: c.is_primary,
      }));

  // Filtra warning "Nessuna regola valida" se abbiamo comunque uno storico coerente per la stagione
  const warningsToShow = (computed?.warnings || []).filter((w) => {
    if (currentSeasonAssignments.length > 0 && /nessuna regola valida/i.test(w)) return false;
    return true;
  });

  return (
    <Card className="p-5 max-w-3xl space-y-4" data-testid="athlete-category-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#0052FF]" />
          <div>
            <div className="font-heading font-semibold">Categoria stagionale</div>
            <div className="text-xs text-zinc-500" data-testid="cat-season-name">Stagione {effectiveSeason?.name || "—"}</div>
          </div>
        </div>
        {canEdit && (
          <Button data-testid="recompute-category-btn" onClick={recompute} disabled={loading} size="sm" variant="outline">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Ricalcolo…" : "Ricalcola"}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
        <Info label="Data di nascita" value={athlete?.birth_date || "—"} />
        <Info label="Età" value={age !== null ? `${age} anni` : "—"} />
        <Info label="Sesso" value={athlete?.gender === "F" ? "Femminile" : athlete?.gender === "M" ? "Maschile" : "—"} />
        <Info label="Settore" value={isParalympic ? "Paralimpico" : "Olimpico"} highlight={isParalympic} />
      </div>

      <div className="pt-3 border-t border-zinc-100">
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Categoria principale + aggiuntive ammesse</div>
        <CategoryBadges categories={displayCategories} />
        {warningsToShow.length > 0 && (
          <ul className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 space-y-1" data-testid="category-warnings">
            {warningsToShow.map((w, i) => <li key={i}>⚠ {w}</li>)}
          </ul>
        )}
      </div>

      {isParalympic && (
        <div className="pt-3 border-t border-zinc-100 text-sm">
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Disciplina paralimpica</div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider px-2 py-1 rounded bg-[#0052FF] text-white">{paralympic.discipline || "—"}</span>
            {paralympic.discipline === "carrozzina" && paralympic.functional_classification && (
              <span className="text-[11px] uppercase tracking-wider px-2 py-1 rounded bg-zinc-900 text-white">
                Classificazione {paralympic.functional_classification}
              </span>
            )}
            {paralympic.classification_status && (
              <span className="text-[11px] uppercase tracking-wider px-2 py-1 rounded border border-zinc-300">{paralympic.classification_status}</span>
            )}
          </div>
        </div>
      )}

      {isAdmin && otherSeasonAssignments.length > 0 && (
        <div className="pt-3 border-t border-zinc-100">
          <button
            data-testid="toggle-history"
            className="flex items-center gap-1 text-xs uppercase tracking-wider text-zinc-500 hover:text-black"
            onClick={() => setShowHistory((v) => !v)}
          >
            {showHistory ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Storico stagioni precedenti ({new Set(otherSeasonAssignments.map((c) => c.season_id)).size})
          </button>
          {showHistory && (
            <div className="mt-2 space-y-1 text-xs" data-testid="history-list">
              {Array.from(new Set(otherSeasonAssignments.map((c) => c.season_id))).map((sid) => {
                const items = otherSeasonAssignments.filter((c) => c.season_id === sid);
                const seasonName = items[0]?.season_name;
                return (
                  <div key={sid} className="flex items-center gap-2 py-1 border-b border-zinc-100 last:border-0">
                    <span className="font-mono text-zinc-500 w-24">{seasonName || "—"}</span>
                    <CategoryBadges categories={items.map((c) => ({
                      category_id: c.category_id, category_code: c.category_code, display_name: c.display_name, is_primary: c.is_primary,
                    }))} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function Info({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`font-medium ${highlight ? "text-[#0052FF]" : ""}`}>{value}</div>
    </div>
  );
}
